import { useEffect, useRef, useState } from 'react';
import { MyAppsIcon } from '../myAppsIcon';
import type { MyAppsSearchResult } from '../myAppsSearch';
import './MyAppsRecentMenu.css';

export interface MyAppsRecentMenuProps {
  recent: readonly MyAppsSearchResult[];
  onSelect: (result: MyAppsSearchResult) => void;
  onClearRecent: () => void;
}

export function MyAppsRecentMenu({ recent, onSelect, onClearRecent }: MyAppsRecentMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;

    function onDocumentMouseDown(event: MouseEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    }

    function onDocumentKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false);
    }

    document.addEventListener('mousedown', onDocumentMouseDown);
    document.addEventListener('keydown', onDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
      document.removeEventListener('keydown', onDocumentKeyDown);
    };
  }, [menuOpen]);

  function selectRecent(result: MyAppsSearchResult) {
    setMenuOpen(false);
    onSelect(result);
  }

  return (
    <div className="my-apps-recent-menu" ref={menuRef}>
      <button
        type="button"
        className="toolbar-icon-btn"
        title="Recently used tools"
        aria-label="Recently used tools"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((open) => !open)}
      >
        <i className="dx-icon dx-icon-clock" aria-hidden />
      </button>

      {menuOpen ? (
        <div className="my-apps-recent-menu__panel" role="menu" aria-label="Recently used tools">
          <div className="my-apps-recent-menu__heading">Recently used</div>
          {recent.length > 0 ? (
            <>
              <div className="my-apps-recent-menu__items">
                {recent.map((result) => (
                  <button
                    key={result.id}
                    type="button"
                    role="menuitem"
                    className="my-apps-recent-menu__item"
                    onClick={() => selectRecent(result)}
                  >
                    <MyAppsIcon icon={result.icon} />
                    <span>{result.title}</span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                role="menuitem"
                className="my-apps-recent-menu__clear"
                onClick={onClearRecent}
              >
                Clear recent
              </button>
            </>
          ) : (
            <p className="my-apps-recent-menu__empty">No recent tools</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
